import { DEFAULT_LOCALE, Locale, messages } from './messages';

/** Maps Thai server error messages to translation keys. */
export const ERROR_MESSAGE_KEYS: Record<string, string> = {
  'กรุณาระบุชื่องาน': 'errors.taskTitleRequired',
  'กรุณาระบุชื่อโปรเจกต์': 'errors.projectNameRequired',
  'กรุณาระบุชื่อรายการย่อย': 'errors.subtaskTitleRequired',
  'ไม่พบงาน': 'errors.taskNotFound',
  'UNAUTHORIZED: ต้องเข้าสู่ระบบก่อนใช้งาน': 'errors.unauthorized',
  'กรุณาระบุชื่อหมวดหมู่': 'errors.categoryNameRequired',
  'รูปแบบสีไม่ถูกต้อง': 'errors.invalidColor',
  'รูปแบบไฟล์ไม่ถูกต้อง (ต้องเป็น data URL แบบ base64)': 'errors.invalidFileFormat',
  'อนุญาตเฉพาะไฟล์รูปภาพ (PNG, JPG, GIF, WebP, AVIF)': 'errors.imagesOnly',
  'ขนาดไฟล์ต้องไม่เกิน 10 MB': 'errors.fileTooLarge',
  'ประเภทหมวดหมู่ไม่ถูกต้อง': 'errors.invalidCategoryType',
  'มีหมวดหมู่นี้อยู่แล้ว': 'errors.categoryExists',
  'ไม่พบหมวดหมู่': 'errors.categoryNotFound',
  'รูปแบบไฟล์สลิปไม่ถูกต้อง (ต้องเป็น data URL แบบ base64)': 'errors.invalidSlipFormat',
  'อนุญาตเฉพาะไฟล์รูปภาพสลิป (PNG, JPG, GIF, WebP, AVIF)': 'errors.slipImagesOnly',
  'ขนาดไฟล์สลิปต้องไม่เกิน 10 MB': 'errors.slipTooLarge',
  'ประเภทบัญชีไม่ถูกต้อง (ต้องเป็น income หรือ expense)': 'errors.invalidAccountType',
  'จำนวนเงินต้องเป็นตัวเลขมากกว่า 0': 'errors.amountPositive',
  'วันที่ทำรายการไม่ถูกต้อง': 'errors.invalidTransactionDate',
  'กรุณาระบุหมวดหมู่': 'errors.categoryRequired',
  'แนบสลิปได้เฉพาะรายจ่ายเท่านั้น': 'errors.slipExpenseOnly',
  'ไม่พบรายการธุรกรรม': 'errors.transactionNotFound',
  'กรุณาระบุชื่อรายการ': 'errors.itemTitleRequired',
  'กรุณาเลือกหมวดหมู่': 'errors.categorySelectRequired',
  'วันที่ครบกำหนดต้องอยู่ระหว่าง 1–31': 'errors.invalidDayOfMonth',
  'ไม่พบรายการ': 'errors.itemNotFound',
  'รายการนี้ถูกปิดใช้งาน': 'errors.itemInactive',
  'บันทึกรายการนี้ในเดือนนี้แล้ว': 'errors.alreadyRecordedThisMonth',
  'กรุณาระบุชื่อกิจกรรม': 'errors.eventTitleRequired',
  'ไม่พบกิจกรรม': 'errors.eventNotFound',
  'วันที่กิจกรรมไม่ถูกต้อง': 'errors.invalidEventDate',
  'วันสิ้นสุดต้องไม่มาก่อนวันเริ่มต้น': 'errors.endBeforeStart',
  'รูปแบบวันที่ไม่ถูกต้อง': 'errors.invalidDateFormat',
  'กรุณาระบุเวอร์ชันและหัวข้ออัปเดต': 'errors.changelogRequired',
  'ส่วนแบ่งต้องเป็นตัวเลขระหว่าง 0 ถึง 100': 'errors.invalidShare',
  'กรุณาระบุชื่อรูป': 'errors.imageNameRequired',
  'ไม่พบรูปภาพ': 'errors.imageNotFound',
};

const ERROR_MESSAGE_PATTERNS: Array<{
  test: (message: string) => boolean;
  key: string;
  params: (message: string) => Record<string, string>;
}> = [
  {
    test: (m) => m.startsWith('สถานะงานไม่ถูกต้อง:'),
    key: 'errors.invalidTaskStatus',
    params: (m) => ({ status: m.slice('สถานะงานไม่ถูกต้อง:'.length).trim() }),
  },
  {
    test: (m) => m.startsWith('ไม่สามารถลบได้ — มี ') && m.includes(' รายการที่ใช้หมวดหมู่นี้อยู่'),
    key: 'errors.categoryInUse',
    params: (m) => {
      const match = m.match(/มี (\d+) รายการ/);
      return { count: match?.[1] ?? '0' };
    },
  },
  {
    test: (m) => m.startsWith('ไม่อนุญาตให้แก้ไขการตั้งค่า:'),
    key: 'errors.settingNotAllowed',
    params: (m) => ({ key: m.slice('ไม่อนุญาตให้แก้ไขการตั้งค่า:'.length).trim() }),
  },
  {
    test: (m) => m.startsWith('ไม่สามารถกำหนดส่วนแบ่งได้รวมกันเกิน 100%'),
    key: 'errors.shareTotalExceeded',
    params: (m) => {
      const serverMatch = m.match(/เซิร์ฟเวอร์ ([\d.]+)%/);
      const otherMatch = m.match(/ทีมงานคนอื่น ([\d.]+)%/);
      return {
        serverShare: serverMatch?.[1] ?? '0',
        otherTotal: otherMatch?.[1] ?? '0',
      };
    },
  },
];

function applyParams(template: string, params: Record<string, string>): string {
  return Object.entries(params).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, value),
    template
  );
}

export function translateError(locale: Locale, message: string): string {
  const key = ERROR_MESSAGE_KEYS[message];
  if (key) {
    return messages[locale]?.[key] ?? messages[DEFAULT_LOCALE][key] ?? message;
  }

  for (const pattern of ERROR_MESSAGE_PATTERNS) {
    if (pattern.test(message)) {
      const template =
        messages[locale]?.[pattern.key] ?? messages[DEFAULT_LOCALE][pattern.key] ?? message;
      return applyParams(template, pattern.params(message));
    }
  }

  if (message.startsWith('UNAUTHORIZED:')) {
    return messages[locale]?.['errors.unauthorized'] ?? message;
  }
  return message;
}
